<template>
  <f7-page class="poolit-login-screen" name="login">
    <div class="poolit-login-screen-content">
      <!-- Animated Background Elements -->
      <div class="poolit-background-elements">
        <div class="poolit-bg-element poolit-bg-element-1" />
        <div class="poolit-bg-element poolit-bg-element-2" />
        <div class="poolit-bg-element poolit-bg-element-3" />
        <div class="poolit-bg-element poolit-bg-element-4" />
      </div>

      <!-- Pre-auth language toggle (UX-25) — lets a visitor pick their language
           before anything else. Labels are language autonyms (fixed forms). -->
      <button
        class="poolit-lang-toggle"
        :aria-label="$t('auth.switchLanguage')"
        @click="toggleLanguage"
      >
        <f7-icon
          f7="globe"
          size="16"
          aria-hidden="true"
        ></f7-icon>
        <span>{{ nextLanguageLabel }}</span>
      </button>

      <!-- Main Content Container -->
      <div class="poolit-main-content">
        <!-- Logo and Welcome Section -->
        <div class="poolit-header-section">
          <div class="poolit-logo-container">
            <img
              src="@/assets/poolit-logo.svg"
              alt="PoolIt Logo"
              class="poolit-logo-image"
            />
          </div>

          <div class="poolit-welcome-section">
            <h1 class="poolit-welcome-title">
              {{ $t('auth.welcome') }}
            </h1>
            <p class="poolit-welcome-subtitle">
              {{ $t('auth.welcomeMessage') }}
            </p>
          </div>
        </div>

        <!-- Login Methods Card -->
        <f7-card class="poolit-actions-card">
          <f7-card-content class="poolit-actions-content">
            <!--
              "Sign in with Telegram" button. The OIDC flow is server-side: this
              <a> follows a backend route that generates PKCE parameters and
              302's to oauth.telegram.org/auth. After the user confirms,
              Telegram redirects back to the backend callback which sets the
              Sanctum session cookie and 302's to /home/. Hidden in TMA where
              init_data already authenticates the user.

              `external` class is load-bearing: F7's link click handler
              (clicks.externalLinks default selector) otherwise intercepts
              cross-origin <a> clicks inside <f7-page> and feeds the href to
              router.navigate() as a relative path → /login/<full-href> 404.
            -->
            <a
              v-if="!isInTma"
              class="poolit-telegram-btn external"
              :href="telegramOidcUrl"
            >
              <f7-icon f7="paperplane_fill" class="poolit-telegram-btn-icon"></f7-icon>
              <span class="poolit-telegram-btn-text">
                {{ $t('auth.signInWithTelegram') }}
              </span>
            </a>

            <!-- Expandable Email/Password Section -->
            <div class="poolit-email-section">
              <f7-button
                large
                outline
                class="poolit-email-toggle-btn"
                :class="{ 'active': showEmailForm }"
                @click="toggleEmailForm"
              >
                <div class="poolit-btn-content">
                  <f7-icon
                    :f7="showEmailForm ? 'chevron_up' : 'envelope_fill'"
                    class="poolit-btn-icon"
                  ></f7-icon>
                  <span class="poolit-btn-text">
                    {{ $t('auth.loginWithEmail') }}
                  </span>
                </div>
              </f7-button>

              <!-- Email Form (Collapsible) -->
              <div
                class="poolit-email-form-wrapper"
                :class="{ 'expanded': showEmailForm }"
              >
                <!-- Real labels, not placeholder-only (UX-P6 / WCAG 3.3.2) -->
                <f7-list no-hairlines class="poolit-email-form">
                  <f7-list-input
                    v-model:value="credentials.email"
                    type="email"
                    :label="$t('auth.email')"
                    floating-label
                    :placeholder="$t('auth.emailPlaceholder')"
                    :disabled="isLoading"
                    clear-button
                    @keyup.enter="handleEmailLogin"
                  >
                    <template #media>
                      <f7-icon f7="envelope"></f7-icon>
                    </template>
                  </f7-list-input>

                  <f7-list-input
                    v-model:value="credentials.password"
                    type="password"
                    :label="$t('auth.password')"
                    floating-label
                    :placeholder="$t('auth.passwordPlaceholder')"
                    :disabled="isLoading"
                    clear-button
                    @keyup.enter="handleEmailLogin"
                  >
                    <template #media>
                      <f7-icon f7="lock"></f7-icon>
                    </template>
                  </f7-list-input>
                </f7-list>

                <f7-button
                  large
                  fill
                  class="poolit-email-submit-btn"
                  :disabled="isLoading || !isFormValid"
                  @click="handleEmailLogin"
                >
                  <div class="poolit-btn-content">
                    <f7-preloader
                      v-if="isLoading && authMethod === 'email'"
                      size="20px"
                      class="poolit-btn-icon"
                    ></f7-preloader>
                    <span class="poolit-btn-text">
                      {{ isLoading && authMethod === 'email' ? $t('general.loading') : $t('auth.signIn') }}
                    </span>
                  </div>
                </f7-button>
              </div>
            </div>

            <!-- Error Message — the single error channel (UX-27): inline only,
                 announced to AT, cleared as soon as the user edits a field -->
            <div
              v-if="loginError"
              class="poolit-error-message"
              role="alert"
            >
              <f7-icon f7="exclamationmark_triangle_fill"></f7-icon>
              <span>{{ loginError }}</span>
            </div>

            <!-- Help Text -->
            <div class="poolit-help-text">
              <p>{{ $t('auth.helpText') }}</p>
            </div>
          </f7-card-content>
        </f7-card>
      </div>
    </div>
  </f7-page>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { launchLandingPath } from '@/clubs/manifest.js';
import { useAuth } from '@/composables/useAuth';
import { isTma } from '@/platform/index.js';
import { useAuthStore } from '@/stores/authStore';
import { useLanguageStore } from '@/stores/useLanguageStore.js';

// ===== PROPS =====
const props = defineProps({
  f7router: {
    type: Object,
    default: null,
  },
  f7route: {
    type: Object,
    default: null,
  },
});

// ===== COMPOSABLES =====
const { t } = useI18n();
const { login, isLoading, error } = useAuth();
const languageStore = useLanguageStore();
const authStore = useAuthStore();

// ===== STATE =====
const credentials = ref({
  email: '',
  password: '',
});
const showEmailForm = ref(false);
const authMethod = ref(null); // 'email' (Telegram OIDC is a full-page redirect)
const isInTma = isTma();
// Local error surface (UX-27). useAuth().error is a read-only computed over the
// store — assigning to it is a silent no-op — so the inline message owns its
// own ref and copies the store message on failure.
const loginError = ref(null);

// ===== COMPUTED =====
const isFormValid = computed(() => {
  return credentials.value.email.trim() && credentials.value.password.trim();
});

// Language autonyms — fixed self-referential names, intentionally not translated.
const nextLanguageLabel = computed(() =>
  languageStore.currentLanguage === 'ar' ? 'English' : 'العربية',
);

function toggleLanguage() {
  languageStore.setLanguage(languageStore.currentLanguage === 'ar' ? 'en' : 'ar');
}

// Backend route that initiates the OIDC Authorization Code + PKCE flow.
// The backend generates code_verifier/code_challenge/state/nonce, stores
// them in the session, and 302's to oauth.telegram.org/auth. After Telegram
// confirms, the backend's callback exchanges the code, verifies the id_token
// against Telegram's JWKS, sets the Sanctum session cookie, and 302's to
// /home/ (or back to /login/?error=... on failure).
const telegramOidcUrl = computed(
  () => `${import.meta.env.VITE_API_URL}/auth/telegram-oidc/redirect`,
);

// ===== LIFECYCLE HOOKS =====
//
// Surface OIDC callback errors. When the backend's /auth/telegram-oidc/callback
// route can't complete the dance (state mismatch, denied consent, JWT verify
// fail, expired nonce, token-exchange 4xx) it 302's back here with
// ?error=<code>&error_description=<human readable>. Toast it once, then strip
// the params so a refresh doesn't re-toast.
onMounted(() => {
  const params = new URLSearchParams(window.location.search);
  const errorDescription = params.get('error_description') || params.get('error');
  if (errorDescription) {
    // Single error channel (UX-27): surface inline instead of a transient toast.
    loginError.value = errorDescription;
    window.history.replaceState({}, '', window.location.pathname);
  }
});

// UX-27: a stale error must not outlive the input that caused it.
watch(
  credentials,
  () => {
    if (loginError.value) loginError.value = null;
  },
  { deep: true },
);

// ===== AUTH METHODS =====

async function handleEmailLogin() {
  if (!isFormValid.value) return;

  authMethod.value = 'email';
  const success = await login(credentials.value);
  authMethod.value = null;
  handleAuthResult(success);
}

// ===== UTILITY METHODS =====

function handleAuthResult(success) {
  if (success) {
    showSuccessAndNavigate();
  } else {
    // Copy the store's message into the inline surface — no toast: one error
    // channel (UX-27).
    loginError.value = error.value || t('auth.loginFailed');
  }
}

function showSuccessAndNavigate() {
  f7.toast.show({
    text: t('auth.loginSuccess'),
    position: 'center',
    closeTimeout: 2000,
    cssClass: 'color-green',
  });

  // Twin of LaunchPage's landingPath(): a collector with no club membership
  // lands on his day, everyone else on /home/.
  setTimeout(() => {
    const path = launchLandingPath({
      isCollector: authStore.isCollector,
      hasActiveMembership: authStore.hasActiveMembership,
    });
    navigateTo(path, { clearPreviousHistory: true });
  }, 500);
}

function navigateTo(path, options = {}) {
  if (props.f7router) {
    props.f7router.navigate(path, options);
  } else {
    f7.views.main.router.navigate(path, options);
  }
}

function toggleEmailForm() {
  showEmailForm.value = !showEmailForm.value;
  if (showEmailForm.value) {
    loginError.value = null;
  }
}
</script>

<style scoped>
/* ===== BASE STYLES ===== */
.poolit-login-screen {
  /* Brand backdrop: deep indigo → brand purple → sky (replaces the off-brand
     indigo/pink gradient; white headline keeps ≥3:1 large-text contrast) */
  background: linear-gradient(
    135deg,
    var(--poolit-primary-800) 0%,
    var(--poolit-primary-500) 55%,
    var(--poolit-secondary-500) 100%
  );
  /* % of the app root, not `vh` — in Telegram the root is the visible area
     (see the html.telegram-webapp rule in app.css). The chain to <html> is
     definite: .views/.view/.page are all height:100%. */
  min-height: 100%;
  overflow: hidden;
}

/* ===== LANGUAGE TOGGLE ===== */
.poolit-lang-toggle {
  position: absolute;
  z-index: 2;
  top: calc(var(--f7-safe-area-top, 0px) + var(--poolit-spacing-md));
  inset-inline-end: var(--poolit-spacing-md);
  display: inline-flex;
  align-items: center;
  gap: var(--poolit-spacing-xs);
  min-height: 44px;
  padding: 0 var(--poolit-spacing-md);
  border: none;
  border-radius: var(--poolit-radius-full);
  background: rgba(255, 255, 255, 0.18);
  color: var(--poolit-text-on-primary);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  backdrop-filter: blur(8px);
}

.poolit-lang-toggle:active {
  background: rgba(255, 255, 255, 0.3);
}

.poolit-login-screen-content {
  position: relative;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

/* ===== BACKGROUND ANIMATION ===== */
.poolit-background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
}

.poolit-bg-element {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 20s infinite ease-in-out;
}

.poolit-bg-element-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.poolit-bg-element-2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  right: -50px;
  animation-delay: 5s;
}

.poolit-bg-element-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  right: -75px;
  animation-delay: 10s;
}

.poolit-bg-element-4 {
  width: 250px;
  height: 250px;
  bottom: 20%;
  left: -125px;
  animation-delay: 15s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(20px, 20px) scale(1.1);
  }
  50% {
    transform: translate(-10px, 30px) scale(0.9);
  }
  75% {
    transform: translate(30px, -20px) scale(1.05);
  }
}

/* ===== MAIN CONTENT ===== */
.poolit-main-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
}

/* ===== HEADER SECTION ===== */
.poolit-header-section {
  text-align: center;
  margin-bottom: 2rem;
}

.poolit-logo-container {
  margin-bottom: 1.5rem;
  animation: fadeInDown 0.8s ease-out;
}

.poolit-logo-image {
  width: 180px;
  height: 80px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
}

.poolit-welcome-section {
  animation: fadeInUp 0.8s ease-out;
}

.poolit-welcome-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.poolit-welcome-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 400;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== ACTIONS CARD ===== */
.poolit-actions-card {
  margin: 0;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.5s ease-out 0.3s both;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.poolit-actions-content {
  padding: 2rem 1.5rem;
}

/* ===== BUTTONS BASE ===== */
.poolit-btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.poolit-btn-icon {
  font-size: 20px;
}

.poolit-btn-text {
  font-size: 16px;
  font-weight: 600;
}

/* ===== TELEGRAM SIGN-IN BUTTON =====
   Telegram brand blues, darkened to the AA-safe pair from the token SSOT —
   the classic #54a9eb/#0088cc gradient put white text at 2.1–3.0:1 (UX-P2).
   The element is a real <a> so the browser handles the navigation to the
   backend OIDC redirect route — no JS, no popup blocker risk, no
   COOP/postMessage gotchas.
*/
.poolit-telegram-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 50px;
  margin-bottom: 1rem;
  padding: 0 1rem;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--poolit-telegram-600) 0%, var(--poolit-telegram-700) 100%);
  color: #ffffff !important;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 4px 16px rgba(0, 102, 153, 0.3);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.poolit-telegram-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 102, 153, 0.4);
}

.poolit-telegram-btn-icon {
  font-size: 20px;
}

.poolit-telegram-btn-text {
  font-size: 16px;
  font-weight: 600;
}

/* ===== EMAIL SECTION ===== */
.poolit-email-section {
  margin-bottom: 1rem;
}

.poolit-email-toggle-btn {
  height: 50px;
  border-radius: 14px;
  border: 2px solid var(--poolit-primary-700);
  color: var(--poolit-primary-700);
  transition: all 0.3s ease;
  width: 100%;
}

.poolit-email-toggle-btn.active {
  background: rgba(var(--poolit-primary-rgb), 0.05);
  border-color: var(--poolit-primary-800);
}

.poolit-email-toggle-btn:active {
  transform: scale(0.98);
}

/* ===== EMAIL FORM (COLLAPSIBLE) ===== */
.poolit-email-form-wrapper {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease, opacity 0.3s ease, margin-top 0.3s ease;
  opacity: 0;
}

.poolit-email-form-wrapper.expanded {
  max-height: 400px;
  opacity: 1;
  margin-top: 1rem;
}

.poolit-email-form {
  margin: 0 0 1rem 0;
  background: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
}

.poolit-email-form :deep(.item-inner) {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.poolit-email-form :deep(.item-input-wrap) {
  margin-top: 0;
}

.poolit-email-form :deep(input) {
  font-size: 15px;
}

.poolit-email-submit-btn {
  height: 50px;
  border-radius: 14px;
  /* primary-600→700 keeps white label ≥4.5:1 across the whole gradient */
  background: linear-gradient(135deg, var(--poolit-primary-600) 0%, var(--poolit-primary-700) 100%);
  box-shadow: 0 4px 16px rgba(var(--poolit-primary-rgb), 0.3);
  transition: all 0.3s ease;
}

.poolit-email-submit-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(var(--poolit-primary-rgb), 0.4);
}

.poolit-email-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== ERROR MESSAGE ===== */
.poolit-error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--poolit-error-50);
  border: 1px solid var(--poolit-error-100);
  border-radius: 10px;
  color: var(--poolit-error-700);
  font-size: 14px;
  margin-top: 1rem;
  animation: shake 0.4s ease;
}

.poolit-error-message i {
  font-size: 18px;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

/* ===== HELP TEXT ===== */
.poolit-help-text {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--poolit-border-color);
}

.poolit-help-text p {
  margin: 0;
  font-size: 13px;
  color: var(--poolit-text-muted);
  line-height: 1.5;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 360px) {
  .poolit-welcome-title {
    font-size: 24px;
  }

  .poolit-welcome-subtitle {
    font-size: 14px;
  }

  .poolit-actions-content {
    padding: 1.5rem 1rem;
  }
}
</style>
